import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { Skeleton } from '@shared/ui';
import { captureError } from '@shared/lib/observability';

interface FeatureRow {
  feature_key: string;
  label_ru: string;
  category: string;
  has_api_cost: boolean;
  from_org: boolean;
  override_state: 'grant' | 'deny' | null;
  expires_at: string | null;
  effective: boolean;
}
interface ProfileAccess {
  access_status: string | null;
  access_expires_at: string | null;
  organization_id: string | null;
}
interface OrgRow { id: string; name: string; access_status: string; max_devices: number | null }
interface ScopeRow {
  id: string;
  advertiser_id: string | null;
  brand_id: string | null;
  relation: string;
  advertisers: { name: string } | null;
  brands: { name: string } | null;
}
interface SessionRow {
  session_id: string;
  user_agent: string | null;
  ip: string | null;
  created_at: string;
  updated_at: string;
  not_after: string | null;
}

const cleanIp = (raw: string | null) => (raw ? raw.replace(/\/\d+$/, '').replace(/^::ffff:/, '') : '—');
function deviceLabel(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const os = /iPhone/i.test(ua) ? 'iPhone' : /iPad/i.test(ua) ? 'iPad' : /Android/i.test(ua) ? 'Android'
    : /Mac OS X/i.test(ua) ? 'Mac' : /Windows/i.test(ua) ? 'Windows' : /Linux/i.test(ua) ? 'Linux' : '';
  const br = /Edg\//i.test(ua) ? 'Edge' : /Chrome/i.test(ua) ? 'Chrome' : /Firefox/i.test(ua) ? 'Firefox'
    : /Safari/i.test(ua) ? 'Safari' : '';
  return [os, br].filter(Boolean).join(' · ') || 'Device';
}

const STATUS_COLOR: Record<string, string> = {
  active: '#2ecc71', pending: '#f1c40f', suspended: '#e74c3c',
};

/**
 * Admin drawer for one user (Access Control Phase 1): approve/suspend, link a
 * company (required for any feature access), toggle per-user feature access, and
 * set an access-expiry date.
 */
export default function UserAccessDrawer({
  userId, displayName, onClose,
}: { userId: string; displayName: string | null; onClose: () => void }) {
  const { t } = useTranslation('admin');
  const invalidate = useInvalidateSupabase();
  const [busy, setBusy] = useState(false);
  const [scopeMode, setScopeMode] = useState<'advertiser' | 'brand'>('advertiser');
  const [scopeSearch, setScopeSearch] = useState('');
  const [scopeRelation, setScopeRelation] = useState<'self' | 'agency'>('agency');

  // Keep this query minimal + embed-free so the status/company NEVER fails to load
  // (an advertiser embed here broke .single() for users with a null advertiser_id,
  // making them wrongly show "pending"). Advertiser name is fetched separately.
  const profileQ = useSupabaseQuery<ProfileAccess>(
    ['admin-user-access-profile', userId],
    async () => supabase.from('profiles')
      .select('access_status, access_expires_at, organization_id')
      .eq('id', userId).single(),
  );
  const featuresQ = useSupabaseQuery<FeatureRow[]>(
    ['admin-user-access-features', userId],
    async () => supabase.rpc('admin_get_user_access', { p_user: userId }),
  );
  const orgsQ = useSupabaseQuery<OrgRow[]>(
    ['admin-organizations'],
    async () => supabase.from('organizations').select('id, name, access_status, max_devices').order('name'),
  );
  const sessionsQ = useSupabaseQuery<SessionRow[]>(
    ['admin-user-sessions', userId],
    async () => supabase.rpc('admin_list_user_sessions', { p_user: userId }),
  );

  const prof = profileQ.data;
  // Real status ONLY when the profile has loaded — never fabricate 'pending' while
  // loading/erroring (that made active users look pending).
  const status = prof?.access_status ?? null;
  const orgId = prof?.organization_id ?? null;
  const org = (orgsQ.data ?? []).find((o) => o.id === orgId) ?? null;

  // Advertiser name for the verified-claim hint — from the user's approved
  // advertiser claim (profiles.advertiser_id was dropped in the 2026-07-18
  // identity refactor; linkage now lives in advertiser_claims). Non-fatal.
  const advQ = useSupabaseQuery<{ advertisers: { name: string } | null } | null>(
    ['admin-user-advertiser', userId],
    async () => supabase.from('advertiser_claims')
      .select('advertisers(name)')
      .eq('user_id', userId).eq('status', 'approved')
      .order('reviewed_at', { ascending: false, nullsFirst: false }).limit(1).maybeSingle(),
  );
  const claimName = advQ.data?.advertisers?.name ?? null;

  // Own-analytics scope of the org: current rows (advertiser-level / brand-level) + search.
  const scopeQ = useSupabaseQuery<ScopeRow[]>(
    ['admin-org-scope', orgId ?? 'none'],
    async () => supabase.from('organization_ad_scope')
      .select('id, advertiser_id, brand_id, relation, advertisers(name), brands(name)')
      .eq('org_id', orgId ?? ''),
    { enabled: !!orgId },
  );
  const scopeSearchQ = useSupabaseQuery<{ id: string; name: string }[]>(
    ['admin-scope-search', scopeMode, scopeSearch],
    async () => supabase.from(scopeMode === 'advertiser' ? 'advertisers' : 'brands')
      .select('id, name').ilike('name', `%${scopeSearch.trim()}%`).order('name').limit(10),
    { enabled: scopeSearch.trim().length >= 2 },
  );

  const refetch = () => {
    invalidate(['admin-user-access-profile', userId]);
    invalidate(['admin-user-access-features', userId]);
    invalidate(['admin-user-sessions', userId]);
    invalidate(['admin-org-scope', orgId ?? 'none']);
    invalidate(['admin-organizations']);
    invalidate(['admin-users']);
  };

  const call = async (fn: string, args: Record<string, unknown>, where: string) => {
    setBusy(true);
    const { error } = await supabase.rpc(fn, args);
    setBusy(false);
    if (error) { captureError(error, { where, userId }); alert(error.message); return false; }
    refetch();
    return true;
  };

  const approve = () => call('admin_approve_user', { p_user: userId, p_role: 'customer' }, 'drawer.approve');
  const setStatus = (s: string) => call('admin_set_access_status', { p_user: userId, p_status: s }, 'drawer.status');
  const setExpiry = (v: string) =>
    call('admin_set_access_expiry', { p_user: userId, p_expires: v ? new Date(v).toISOString() : null }, 'drawer.expiry');

  // Empty id = unlink the company (p_org_id null). The RPC keeps org_role non-null.
  const linkOrg = async (id: string) =>
    call('admin_set_user_organization', { p_user: userId, p_org_id: id || null, p_org_role: 'member' }, 'drawer.link');
  const createOrg = async () => {
    const name = prompt(t('access.newCompanyPrompt', { defaultValue: 'Название компании' }) ?? '');
    if (!name?.trim()) return;
    setBusy(true);
    const { data, error } = await supabase.rpc('admin_create_organization', { p_name: name.trim() });
    setBusy(false);
    if (error) { captureError(error, { where: 'drawer.createOrg' }); alert(error.message); return; }
    invalidate(['admin-organizations']);
    await linkOrg(data as string);
  };

  // Organization-level grant (applies to ALL members of the org) — the primary control.
  const toggleOrgFeature = (f: FeatureRow) => {
    if (!orgId) return;
    if (f.from_org) call('admin_clear_org_feature', { p_org: orgId, p_key: f.feature_key }, 'drawer.clearOrgFeat');
    else call('admin_set_org_feature', { p_org: orgId, p_key: f.feature_key, p_allowed: true, p_expires: null }, 'drawer.setOrgFeat');
  };
  // Per-user override (exception vs the org): inherit | grant | deny.
  const setUserOverride = (f: FeatureRow, v: string) => {
    if (v === 'inherit') call('admin_clear_user_feature', { p_user: userId, p_key: f.feature_key }, 'drawer.clearOverride');
    else call('admin_set_user_feature', { p_user: userId, p_key: f.feature_key, p_allowed: v === 'grant', p_expires: null }, 'drawer.setOverride');
  };

  const setDeviceLimit = (v: string) => {
    if (!orgId) return;
    const n = v.trim() === '' ? null : Math.max(1, parseInt(v, 10) || 1);
    call('admin_set_org_device_limit', { p_org: orgId, p_max: n }, 'drawer.deviceLimit');
  };
  const revokeSession = (id: string) => call('admin_revoke_session', { p_session_id: id }, 'drawer.revokeSession');
  const revokeAllSessions = () => {
    if (confirm(t('access.signOutAllConfirm', { defaultValue: 'Выйти на всех устройствах этого пользователя?' }))) {
      call('admin_revoke_user_sessions', { p_user: userId }, 'drawer.revokeAll');
    }
  };

  const addScope = (targetId: string) => {
    if (!orgId) return;
    if (scopeMode === 'advertiser') {
      call('admin_add_org_scope', { p_org: orgId, p_advertiser_id: targetId, p_brand_id: null, p_relation: 'self', p_channel: null }, 'drawer.addScopeAdv');
    } else {
      call('admin_add_org_scope', { p_org: orgId, p_advertiser_id: null, p_brand_id: targetId, p_relation: scopeRelation, p_channel: null }, 'drawer.addScopeBrand');
    }
    setScopeSearch('');
  };
  const removeScope = (id: string) => call('admin_remove_org_scope', { p_scope_id: id }, 'drawer.removeScope');

  const box: React.CSSProperties = {
    background: 'rgba(10,20,45,0.6)', border: '1px solid rgba(15,45,100,0.5)', borderRadius: 8, padding: 14, marginBottom: 14,
  };
  const label: React.CSSProperties = { fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.6, marginBottom: 8 };
  const btn = (bg: string): React.CSSProperties => ({
    cursor: 'pointer', fontFamily: 'inherit', padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600,
    color: '#eee', background: bg, border: '1px solid rgba(255,255,255,0.15)',
  });

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 440, maxWidth: '100vw', height: '100%', overflowY: 'auto', padding: 20,
          background: '#0a1226', borderLeft: '1px solid rgba(15,45,100,0.6)', opacity: busy ? 0.7 : 1,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{displayName ?? '—'}</div>
          </div>
          <button onClick={onClose} style={btn('rgba(255,255,255,0.08)')}>✕</button>
        </div>

        {/* Why-inert warning: feature grants only take effect when the account is
            active AND linked to an active company. Only shown once the profile loads. */}
        {prof && !(status === 'active' && org && org.access_status === 'active') && (
          <div style={{
            background: 'rgba(241,196,15,0.12)', border: '1px solid rgba(241,196,15,0.4)',
            borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, lineHeight: 1.45,
          }}>
            ⚠ {status !== 'active'
              ? t('access.warnNotActive', { defaultValue: 'Аккаунт не активен — отметки функций ниже не действуют, пока вы не одобрите доступ.' })
              : !org
                ? t('access.warnNoCompany', { defaultValue: 'Компания не привязана — отметки функций ниже НЕ действуют. Сначала привяжите активную компанию.' })
                : t('access.warnOrgInactive', { defaultValue: 'Компания не активна — доступ к функциям не работает, пока компания приостановлена.' })}
          </div>
        )}

        {/* Approval status */}
        <div style={box}>
          <div style={label}>{t('access.status', { defaultValue: 'Статус доступа' })}</div>
          {status === null ? (
            <div style={{ opacity: 0.5, fontSize: 13 }}>
              {profileQ.isLoading ? '…' : t('access.statusUnavailable', { defaultValue: 'не удалось загрузить' })}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLOR[status] ?? '#888' }} />
                <span style={{ fontWeight: 600 }}>{status}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {status !== 'active' && <button disabled={busy} onClick={approve} style={btn('rgba(46,204,113,0.25)')}>{t('access.approve', { defaultValue: 'Одобрить' })}</button>}
                {status === 'active' && <button disabled={busy} onClick={() => setStatus('suspended')} style={btn('rgba(231,76,60,0.25)')}>{t('access.suspend', { defaultValue: 'Приостановить' })}</button>}
                {status === 'suspended' && <button disabled={busy} onClick={() => setStatus('active')} style={btn('rgba(46,204,113,0.25)')}>{t('access.reactivate', { defaultValue: 'Возобновить' })}</button>}
              </div>
            </>
          )}
        </div>

        {/* Company linkage */}
        <div style={box}>
          <div style={label}>{t('access.organization', { defaultValue: 'Организация' })}</div>
          {claimName && (
            <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 8 }}>
              {t('access.verifiedClaim', { defaultValue: 'Подтверждён как рекламодатель' })}: <b>{claimName}</b>
              {' — '}{t('access.claimScopeHint', { defaultValue: 'его бренды подтянутся в scope при привязке компании' })}
            </div>
          )}
          <div style={{ marginBottom: 10, fontWeight: 600 }}>
            {org ? org.name : <span style={{ opacity: 0.5 }}>{t('access.noCompany', { defaultValue: 'Не привязан' })}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={orgId ?? ''} disabled={busy} onChange={(e) => linkOrg(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)', minWidth: 180 }}>
              <option value="">{orgId ? t('access.unlinkCompany', { defaultValue: '✕ Отвязать организацию' }) : t('access.selectCompany', { defaultValue: '— выбрать —' })}</option>
              {(orgsQ.data ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <button disabled={busy} onClick={createOrg} style={btn('rgba(74,158,255,0.2)')}>{t('access.newCompany', { defaultValue: '+ Новая' })}</button>
          </div>
          <div style={{ fontSize: 12, opacity: 0.5, marginTop: 8 }}>{t('access.companyHint', { defaultValue: 'Без активной компании доступ к функциям закрыт.' })}</div>
          {org && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, opacity: 0.85 }}>{t('access.deviceLimit', { defaultValue: 'Лимит устройств' })}:</span>
              <input
                type="number" min={1} disabled={busy}
                defaultValue={org.max_devices ?? ''}
                placeholder={t('access.unlimited', { defaultValue: '∞' })}
                onBlur={(e) => setDeviceLimit(e.target.value)}
                style={{ width: 72, padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)' }}
              />
              <span style={{ fontSize: 11, opacity: 0.5 }}>{t('access.deviceLimitHint', { defaultValue: 'на всю компанию; пусто = без лимита' })}</span>
            </div>
          )}
        </div>

        {/* Own-analytics scope (organization_ad_scope) — what the org sees in My Analytics */}
        {orgId && (
          <div style={box}>
            <div style={label}>{t('access.scope', { defaultValue: 'Доступ к данным (scope)' })}</div>
            {(scopeQ.data ?? []).length === 0 && (
              <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 8 }}>
                {t('access.scopeEmpty', { defaultValue: 'Не задано — «Моя аналитика» будет пустой.' })}
              </div>
            )}
            {(scopeQ.data ?? []).map((s) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                <span style={{ flex: 1, fontSize: 13 }}>
                  {s.advertiser_id
                    ? `🏢 ${s.advertisers?.name ?? '—'} · ${t('access.allBrands', { defaultValue: 'все бренды' })}`
                    : `▪ ${s.brands?.name ?? '—'}`}
                  <span style={{ fontSize: 11, opacity: 0.5 }}> ({s.relation})</span>
                </span>
                <button disabled={busy} onClick={() => removeScope(s.id)} style={btn('rgba(231,76,60,0.18)')}>✕</button>
              </div>
            ))}

            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <select value={scopeMode} disabled={busy}
                onChange={(e) => { setScopeMode(e.target.value as 'advertiser' | 'brand'); setScopeSearch(''); }}
                style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)', fontSize: 12 }}>
                <option value="advertiser">{t('access.scopeAdvertiser', { defaultValue: 'Рекламодатель (все бренды)' })}</option>
                <option value="brand">{t('access.scopeBrand', { defaultValue: 'Бренд' })}</option>
              </select>
              {scopeMode === 'brand' && (
                <select value={scopeRelation} disabled={busy}
                  onChange={(e) => setScopeRelation(e.target.value as 'self' | 'agency')}
                  style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)', fontSize: 12 }}>
                  <option value="agency">agency</option>
                  <option value="self">self</option>
                </select>
              )}
              <input value={scopeSearch} disabled={busy}
                onChange={(e) => setScopeSearch(e.target.value)}
                placeholder={t('access.scopeSearch', { defaultValue: 'поиск (мин. 2 буквы)…' })}
                style={{ flex: 1, minWidth: 140, padding: '5px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)', fontSize: 13 }} />
            </div>

            {scopeSearch.trim().length >= 2 && (
              <div style={{ marginTop: 6, maxHeight: 170, overflowY: 'auto', border: '1px solid rgba(15,45,100,0.4)', borderRadius: 6 }}>
                {(scopeSearchQ.data ?? []).map((x) => (
                  <div key={x.id} onClick={() => !busy && addScope(x.id)}
                    style={{ padding: '6px 10px', cursor: busy ? 'default' : 'pointer', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    + {x.name}
                  </div>
                ))}
                {!scopeSearchQ.isLoading && (scopeSearchQ.data ?? []).length === 0 && (
                  <div style={{ padding: '6px 10px', fontSize: 12, opacity: 0.5 }}>{t('access.scopeNoHits', { defaultValue: 'ничего не найдено' })}</div>
                )}
              </div>
            )}

            <div style={{ fontSize: 11, opacity: 0.5, marginTop: 6 }}>
              {t('access.scopeHint', { defaultValue: 'Что компания видит в «Моей аналитике». Рекламодатель = все его бренды; бренд = только он (для агентств).' })}
            </div>
          </div>
        )}

        {/* Access expiry */}
        <div style={box}>
          <div style={label}>{t('access.expiry', { defaultValue: 'Срок доступа' })}</div>
          <input type="date" disabled={busy}
            defaultValue={prof?.access_expires_at ? prof.access_expires_at.slice(0, 10) : ''}
            onChange={(e) => setExpiry(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)' }} />
        </div>

        {/* Features — ORGANIZATION level (applies to all members). Primary control. */}
        <div style={box}>
          <div style={label}>{t('access.orgFeatures', { defaultValue: 'Функции организации' })}</div>
          {!orgId && (
            <div style={{ fontSize: 12, opacity: 0.5 }}>
              {t('access.orgFeaturesNoOrg', { defaultValue: 'Привяжите организацию, чтобы выдавать функции всем сотрудникам.' })}
            </div>
          )}
          {orgId && featuresQ.isLoading && <Skeleton height={200} />}
          {orgId && (featuresQ.data ?? []).map((f) => (
            <label key={f.feature_key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: busy ? 'default' : 'pointer' }}>
              <input type="checkbox" disabled={busy} checked={f.from_org} onChange={() => toggleOrgFeature(f)} />
              <span style={{ flex: 1 }}>{f.label_ru}</span>
              {f.has_api_cost && <span title="Платный Claude API">💰</span>}
              <span
                title={f.effective ? t('access.effectiveOn', { defaultValue: 'действует у пользователя' }) : t('access.effectiveOff', { defaultValue: 'не действует' })}
                style={{ width: 8, height: 8, borderRadius: '50%', background: f.effective ? '#2ecc71' : 'rgba(255,255,255,0.15)' }}
              />
            </label>
          ))}
          {orgId && (
            <div style={{ fontSize: 11, opacity: 0.5, marginTop: 6 }}>
              {t('access.orgFeaturesHint', { defaultValue: 'Применяется ко всем сотрудникам организации. Зелёная точка — реально действует у этого пользователя.' })}
            </div>
          )}
        </div>

        {/* Per-user overrides — exceptions vs the organization. */}
        <div style={box}>
          <div style={label}>{t('access.userOverrides', { defaultValue: 'Исключения для пользователя' })}</div>
          {(featuresQ.data ?? []).map((f) => (
            <div key={f.feature_key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
              <span style={{ flex: 1 }}>{f.label_ru}</span>
              {f.has_api_cost && <span title="Платный Claude API">💰</span>}
              <select
                disabled={busy}
                value={f.override_state ?? 'inherit'}
                onChange={(e) => setUserOverride(f, e.target.value)}
                style={{ padding: '3px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.04)', color: '#eee', border: '1px solid rgba(15,45,100,0.5)', fontSize: 12 }}
              >
                <option value="inherit">{t('access.ovInherit', { defaultValue: 'как в орг.' })}</option>
                <option value="grant">{t('access.ovGrant', { defaultValue: 'разрешить' })}</option>
                <option value="deny">{t('access.ovDeny', { defaultValue: 'запретить' })}</option>
              </select>
            </div>
          ))}
          <div style={{ fontSize: 11, opacity: 0.5, marginTop: 6 }}>
            {t('access.userOverridesHint', { defaultValue: 'Переопределяет уровень организации только для этого пользователя.' })}
          </div>
        </div>

        {/* Active sessions / devices (IP visibility + revoke) */}
        <div style={box}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={label}>{t('access.sessions', { defaultValue: 'Устройства и сессии' })}</div>
            {(sessionsQ.data ?? []).length > 0 && (
              <button disabled={busy} onClick={revokeAllSessions} style={btn('rgba(231,76,60,0.2)')}>
                {t('access.signOutAll', { defaultValue: 'Выйти везде' })}
              </button>
            )}
          </div>
          {sessionsQ.isLoading && <Skeleton height={80} />}
          {!sessionsQ.isLoading && (sessionsQ.data ?? []).length === 0 && (
            <div style={{ fontSize: 13, opacity: 0.5 }}>{t('access.noSessions', { defaultValue: 'Активных сессий нет' })}</div>
          )}
          {(sessionsQ.data ?? []).map((s) => (
            <div key={s.session_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13 }}>{deviceLabel(s.user_agent)}</div>
                <div style={{ fontSize: 11, opacity: 0.55 }}>
                  {cleanIp(s.ip)} · {new Date(s.updated_at).toLocaleString()}
                </div>
              </div>
              <button disabled={busy} onClick={() => revokeSession(s.session_id)} style={btn('rgba(231,76,60,0.18)')}>
                {t('access.revoke', { defaultValue: 'Отозвать' })}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
